import type { CareerLeader, GameRecord, HallOfFame, ManagerSeasonLine, StreakRecord } from "@averagejoesff/shared";
import type { ReactNode } from "react";
import { formatPoints, formatRecord } from "../../lib/format";
import type { CountingNote } from "./CountingNotes";
import { CountingNotes } from "./CountingNotes";
import type { PeopleLookup } from "./people";
import { ManagerLink, personName } from "./people";

interface HallOfFameViewProps {
  hallOfFame: HallOfFame;
  people: PeopleLookup;
}

/** One leaderboard line, whatever the category: who, the number that earned it, and the context. */
interface Entry {
  key: string;
  personId: string;
  /** A second name credited beside `personId`, for a record two teams share (a game's total). */
  opponentId?: string;
  /** The headline number, e.g. "289.55", "+206.20", "9 wins". */
  value: string;
  /** Muted second line: the score, the weeks. May be empty. */
  detail: string;
  /** Season (and week) it happened. */
  when: string;
}

/**
 * The league's all-time records as a record book: each category is a card
 * with its holder as a large headline number and the runners-up beneath in
 * one aligned list, grouped as titles, single games, streaks, seasons and careers.
 */
export function HallOfFameView({ hallOfFame: hof, people }: HallOfFameViewProps) {
  const score = (g: GameRecord) => `${formatPoints(g.points)}–${formatPoints(g.opponentPoints)}`;
  const gameEntries = (
    list: readonly GameRecord[],
    value: (g: GameRecord) => string,
    // Default: the holder's score against the opponent, naming the opponent.
    show: { detail: (g: GameRecord) => string; bothTeams?: boolean } = { detail: (g) => `${score(g)} vs ${personName(people, g.opponentId)}` },
  ): Entry[] =>
    list.map((g) => ({
      key: `${g.season}-${g.week}-${g.personId}`,
      personId: g.personId,
      opponentId: show.bothTeams ? g.opponentId : undefined,
      value: value(g),
      detail: show.detail(g),
      when: `${g.season} wk ${g.week}${g.gameType === "playoffs" ? " · playoffs" : ""}`,
    }));

  const streakEntries = (list: readonly StreakRecord[], noun: string): Entry[] =>
    list.map((s) => ({
      key: `${s.personId}-${s.season}-${s.endWeek}`,
      personId: s.personId,
      value: `${s.length} ${noun}`,
      detail: `Weeks ${s.startWeek}–${s.endWeek}`,
      when: String(s.season),
    }));

  const seasonEntries = (list: readonly ManagerSeasonLine[]): Entry[] =>
    list.map((l) => ({
      key: `${l.season}-${l.personId}`,
      personId: l.personId,
      value: formatRecord(l.record.wins, l.record.losses, l.record.ties),
      detail: `${formatPoints(l.pointsFor)} points`,
      when: String(l.season),
    }));

  const titleEntries: Entry[] = hof.titles.map((t) => ({
    key: String(t.personId),
    personId: t.personId,
    value: `🏆 ×${t.seasons.length}`,
    detail: t.seasons.join(", "),
    when: "",
  }));

  const plural = (n: number, noun: string) => `${n} ${noun}${n === 1 ? "" : "s"}`;
  const careerEntries = (list: readonly CareerLeader[], value: (l: CareerLeader) => string, detail: (l: CareerLeader) => string = () => ""): Entry[] =>
    list.map((l) => ({ key: l.personId, personId: l.personId, value: value(l), detail: detail(l), when: "" }));

  const margin = (g: GameRecord) => formatPoints(Math.abs(g.points - g.opponentPoints));
  const total = (g: GameRecord) => formatPoints(g.points + g.opponentPoints);

  return (
    <div className="hof">
      {titleEntries.length > 0 && (
        <Group title="Championships">
          <RecordCard title="Most titles" entries={titleEntries} people={people} wide />
          <RecordCard
            title="Most runner-up finishes"
            entries={careerEntries(hof.careers.mostRunnerUps, (l) => `🥈 ×${l.count}`, (l) => l.seasons.join(", "))}
            people={people}
            wide
          />
        </Group>
      )}
      <Group title="Single games">
        <RecordCard
          title="Highest score"
          entries={gameEntries(hof.highestScores, (g) => formatPoints(g.points), { detail: () => "" })}
          people={people}
        />
        <RecordCard
          title="Lowest score"
          entries={gameEntries(hof.lowestScores, (g) => formatPoints(g.points), { detail: () => "" })}
          people={people}
        />
        <RecordCard title="Biggest blowout" entries={gameEntries(hof.biggestBlowouts, (g) => `+${margin(g)}`)} people={people} />
        <RecordCard
          title="Closest game"
          entries={gameEntries(hof.closestGames, (g) => (g.points === g.opponentPoints ? "Tie" : `${margin(g)} pts`))}
          people={people}
        />
        <RecordCard
          title="Highest-scoring game"
          entries={gameEntries(hof.highestScoringGames, total, { detail: score, bothTeams: true })}
          people={people}
        />
      </Group>
      <Group title="Streaks">
        <RecordCard title="Longest win streak" entries={streakEntries(hof.longestWinStreaks, "wins")} people={people} />
        <RecordCard title="Longest losing streak" entries={streakEntries(hof.longestLossStreaks, "losses")} people={people} />
      </Group>
      <Group title="Seasons">
        <RecordCard title="Best regular season" entries={seasonEntries(hof.bestSeasons)} people={people} />
        <RecordCard title="Worst regular season" entries={seasonEntries(hof.worstSeasons)} people={people} />
      </Group>
      <Group title="Careers">
        <RecordCard title="Most wins" entries={careerEntries(hof.careers.mostWins, (l) => plural(l.count, "win"))} people={people} />
        <RecordCard
          title="Most playoff appearances"
          entries={careerEntries(hof.careers.mostPlayoffAppearances, (l) => plural(l.count, "season"))}
          people={people}
        />
        <RecordCard title="Most weekly high scores" entries={careerEntries(hof.careers.mostWeeklyHighs, (l) => plural(l.count, "week"))} people={people} />
        <RecordCard title="Most blowout awards" entries={careerEntries(hof.careers.mostBlowoutAwards, (l) => plural(l.count, "week"))} people={people} />
      </Group>
      <CountingNotes id="hof-notes-title" notes={HOW_COUNTED} />
    </div>
  );
}

/** Each rule below mirrors what `buildHallOfFame` counts; keep the two in step. */
const HOW_COUNTED: readonly CountingNote[] = [
  ["Single games", ["Regular-season and playoff games count; consolation games don't. The current season counts as it's played."]],
  ["Championships", ["Finished seasons only, from each season's final standings."]],
  ["Streaks", ["Each manager's longest run within one season, playoff games included. A streak never crosses seasons, and a tie ends it."]],
  ["Seasons", ["Finished seasons only, ranked by regular-season record, then points scored."]],
  [
    "Careers",
    [
      "Wins include playoff games.",
      "Weekly high scores and blowout awards count regular-season weeks only. The blowout award goes to each week's largest margin of victory, as on the Blowout page.",
      "Playoff appearances count finished seasons in which a team played a playoff game.",
    ],
  ],
  ["Ties", ["Equal marks go to the most recent."]],
];

function Group({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section>
      <h3 className="feature__subhead">{title}</h3>
      <div className="hof__group">{children}</div>
    </section>
  );
}

interface RecordCardProps {
  title: string;
  entries: readonly Entry[];
  people: PeopleLookup;
  /** Spans the whole row, for a list that reads better wide. */
  wide?: boolean;
}

function RecordCard({ title, entries, people, wide = false }: RecordCardProps) {
  if (entries.length === 0) return null;
  return (
    <article className={wide ? "hof-card hof-card--wide" : "hof-card"}>
      <h4 className="hof-card__title">{title}</h4>
      {/* One ordered list from first place down, so a screen reader hears the holder as number one, not as a lead-in. */}
      <ol className="hof-card__list">
        {entries.map((entry, index) => (
          // Every entry shares one layout (rank, who, value) so the numbers line up; the holder is only highlighted.
          <li key={entry.key} className={index === 0 ? "hof-card__row hof-card__row--holder" : "hof-card__row"}>
            <span className="hof-card__who">
              <span className="hof-card__names">
                <ManagerLink personId={entry.personId} people={people} />
                {entry.opponentId && (
                  <>
                    {" & "}
                    <ManagerLink personId={entry.opponentId} people={people} />
                  </>
                )}
              </span>
              {(entry.detail || entry.when) && (
                <span className="hof-card__detail">
                  {entry.detail}
                  {/* Kept whole so a narrow card wraps before "2023 wk 4", not in the middle of it. */}
                  {entry.when && (
                    <>
                      {entry.detail && " · "}
                      <span className="hof-card__when">{entry.when}</span>
                    </>
                  )}
                </span>
              )}
            </span>
            <span className="hof-card__value">{entry.value}</span>
          </li>
        ))}
      </ol>
    </article>
  );
}
